import React, { useState, useEffect, ChangeEvent } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Checkbox, FormControlLabel, Box, Typography, Container, CssBaseline, CircularProgress } from '@mui/material';
import { AppDispatch, RootState } from '../../redux/store/appStore';
import { useNavigate, useParams } from 'react-router-dom';
import { setUpdateProjectStatus, updateProject } from '../../redux/slices/projectSlice/addProjectSlice';
import { STATUS } from '../../utils/status';
import { FormField } from '../inputField/textfield';
import { AddButton } from '../button/button';
import SnackbarNotification from '../snackbar/snackbarNotification';
import RichTextEditor from '../project/textEditor';

interface FormState {
  test_time: number;
  test_max_attempts: number;
  test_passing_score_percentage: number;
  auto_allocate_task_count: number;
  test_question_count: number;
  // enable_question_randomization: boolean;
  enable_question_shuffling: boolean;
  enable_options_shuffling: boolean;
  test_guidelines: string | { html: string; json: string };
  test_question_distribution: {
    easy: number;
    medium: number;
    hard: number;
  },
  is_test_enabled: boolean;
}

const TestControl = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const dispatch = useDispatch<AppDispatch>();

  const [projectDetails, setProjectDetails] = useState<FormState>({
    test_time: 0,
    test_max_attempts: 0,
    test_passing_score_percentage: 0,
    auto_allocate_task_count: 0,
    test_question_count: 0,
    test_question_distribution: {
      easy: 0,
      medium: 0,
      hard: 0
    },
    // enable_question_randomization: false,
    enable_question_shuffling: false,
    enable_options_shuffling: false,
    test_guidelines: {
      html: '',
      json: ''
    },
    is_test_enabled: false
  });
  const updateStatus = useSelector((state: RootState) => state.projects.updateStatus);
  const getStatus = useSelector((state: RootState) => state.projects.getStatus);
  const updateMessage = useSelector((state: RootState) => state.projects.updateProjectMessage);
  const [formErrors, setFormErrors] = useState<{ [key: string]: string }>({});
  const [formSubmitted, setFormSubmitted] = useState<boolean>(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const fetchedProject = useSelector((state: RootState) => state.projects.project as Partial<FormState>);
  const [snackbarSeverity, setSnackbarSeverity] = useState<'success' | 'error' | 'info' | 'warning'>('success');
  const path = window.location.href.includes('/addproject')

  useEffect(() => {
    if (getStatus === STATUS.SUCESS && !path) {
      console.log({ fetchedProject })
      setProjectDetails({
        test_time: fetchedProject.test_time || 0,
        test_max_attempts: fetchedProject.test_max_attempts || 0,
        test_passing_score_percentage: fetchedProject.test_passing_score_percentage || 0,
        auto_allocate_task_count: fetchedProject.auto_allocate_task_count || 0,
        test_question_count: fetchedProject.test_question_count || 0,
        // enable_question_randomization: Boolean(fetchedProject.enable_question_randomization),
        enable_question_shuffling: Boolean(fetchedProject.enable_question_shuffling),
        enable_options_shuffling: Boolean(fetchedProject.enable_options_shuffling),
        test_guidelines: fetchedProject.test_guidelines || '',
        test_question_distribution: {
          easy: fetchedProject.test_question_distribution?.easy || 0,
          medium: fetchedProject.test_question_distribution?.medium || 0,
          hard: fetchedProject.test_question_distribution?.hard || 0,
        },
        is_test_enabled: Boolean(fetchedProject.is_test_enabled),
      });
    }
  }, [getStatus, fetchedProject]);

  const handleFieldChange = (event: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    const regex = /^[0-9\b]+$/;

    if (regex.test(value) || value === "") {
      if (name.startsWith("test_question_distribution.")) {
        const field = name.split(".")[1];
        setFormErrors((prevErrors) => ({
          ...prevErrors,
          [`test_question_distribution.${field}`]: "",
        }));

        setProjectDetails((prevDetails) => ({
          ...prevDetails,
          test_question_distribution: {
            ...prevDetails.test_question_distribution,
            [field]: value,
          },
        }));
      } else {
        setFormErrors((prevErrors) => ({
          ...prevErrors,
          [name]: "",
        }));

        setProjectDetails((prevDetails) => ({
          ...prevDetails,
          [name]: value,
        }));
      }
    }
  };

  const handleCheckboxChange = (event: ChangeEvent<HTMLInputElement>) => {
    const { name, checked } = event.target;

    setProjectDetails((prevDetails) => ({
      ...prevDetails,
      [name]: checked,
    }));
  };

  const handleSnackbar = (message: string, severity: 'success' | 'error' | 'info' | 'warning') => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  };

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  const stripHtml = (html: string) => {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    return doc.body.textContent || "";
  };

  const handleTestGuidelines = (value: string) => {
    if (stripHtml(value) === "") {
      setProjectDetails(prevDetails => ({
        ...prevDetails, test_guidelines: stripHtml(value)
      }))

      return;
    } else {

      setProjectDetails(prevDetails => ({
        ...prevDetails, test_guidelines: value
      }))
    }
  }

  const handleFormSubmit = (event: any) => {
    event.preventDefault();
    setFormSubmitted(true);

    console.log({ "condition": projectDetails?.test_question_distribution.easy || projectDetails.test_question_distribution.easy < 0, 'val1': projectDetails?.test_question_distribution.easy, 'val2': projectDetails.test_question_distribution.easy < 0 })
    console.log({ "condition": projectDetails?.test_question_distribution.medium || projectDetails.test_question_distribution.medium < 0, 'val1': projectDetails?.test_question_distribution.medium, 'val2': projectDetails.test_question_distribution.medium < 0 })
    console.log({ "condition": projectDetails?.test_question_distribution.hard || projectDetails.test_question_distribution.hard < 0, 'val1': projectDetails?.test_question_distribution.hard, 'val2': projectDetails.test_question_distribution.hard < 0 })

    const validateProjectDetails = (): { [key: string]: string } => {
      const errors: { [key: string]: string } = {};

      if (!projectDetails?.test_max_attempts || projectDetails.test_max_attempts < 1) {
        errors.test_max_attempts = 'Max Attempts count must be a positive number greater than 0';
      }
      if (!projectDetails?.test_passing_score_percentage || projectDetails.test_passing_score_percentage < 0 || projectDetails.test_passing_score_percentage > 100) {
        errors.test_passing_score_percentage = 'Passing score % must be positive and less than 100';
      }
      if (!projectDetails?.test_time || projectDetails.test_time <= 0) {
        errors.test_time = 'Test Time must be a positive number greater than 0';
      }
      if (!projectDetails?.test_question_count || projectDetails.test_question_count < 1) {
        errors.test_question_count = 'Atleast 1 Test Question Count is Required';
      }
      if (isNaN(parseInt(projectDetails.test_question_distribution.easy.toString())) || parseInt(projectDetails.test_question_distribution.easy.toString()) < 0) {
        errors['test_question_distribution.easy'] = 'Easy Test Question Count is Required';
      }

      if (isNaN(parseInt(projectDetails.test_question_distribution.medium.toString())) || parseInt(projectDetails.test_question_distribution.medium.toString()) < 0) {
        errors['test_question_distribution.medium'] = 'Medium Test Question Count is Required';
      }

      if (isNaN(parseInt(projectDetails.test_question_distribution.hard.toString())) || parseInt(projectDetails.test_question_distribution.hard.toString()) < 0) {
        errors['test_question_distribution.hard'] = 'Hard Test Question Count is Required';
      }
      // const totalQuestions = Number(projectDetails.test_question_count);
      // const easy = Number(projectDetails.test_question_distribution.easy) || 0;
      // const medium = Number(projectDetails.test_question_distribution.medium) || 0;
      // const hard = Number(projectDetails.test_question_distribution.hard) || 0;

      // if (easy + medium + hard !== totalQuestions) {
      //   errors['test_question_distribution.easy'] = 'Sum of Easy, Medium, and Hard questions must be equal to Total Question Count';
      //   errors['test_question_distribution.medium'] = 'Sum of Easy, Medium, and Hard questions must be equal to Total Question Count';
      //   errors['test_question_distribution.hard'] = 'Sum of Easy, Medium, and Hard questions must be equal to Total Question Count';
      // }
      if (projectDetails?.auto_allocate_task_count === undefined || projectDetails.auto_allocate_task_count < 0) {
        errors.auto_allocate_task_count = 'Task Count must be a positive number';
      }
      console.log(projectDetails)
      // if (!projectDetails?.test_guidelines?.trim()) {
      //   errors.test_guidelines = 'Test Guideline is Required';
      // }
      // if (!projectDetails?.test_guidelines || !projectDetails.test_guidelines.html?.trim()) {
      //   errors.test_guidelines = 'Test Guideline is Required';
      // }

      return errors;
    };

    const errors = validateProjectDetails();

    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }

    const changedFields = Object.keys(projectDetails).reduce((acc, key) => {
      const fetchedValue = fetchedProject[key as keyof FormState];
      const newValue = projectDetails[key as keyof FormState];

      if (key === "test_question_distribution") {
        const distributionChanges = Object.keys(newValue as { [key: string]: number }).reduce((distAcc, distKey) => {
          distAcc[distKey] = (newValue as { [key: string]: number })?.[distKey] !== (fetchedValue as { [key: string]: number })?.[distKey]
            ? Number((newValue as { [key: string]: number })[distKey])
            : Number((fetchedValue as { [key: string]: number })[distKey]);
          return distAcc;
        }, {} as { [key: string]: number });

        acc[key] = distributionChanges;
      }
      else if (key === "test_guidelines") {
        if (typeof newValue === "object" && newValue !== null && 'html' in newValue) {
          const htmlString = newValue.html || "";
          const plainText = htmlString.replace(/<[^>]+>/g, "").trim();
          acc[key] = plainText;
        } else {
          acc[key] = newValue;
        }
      }
      else if (
        key in fetchedProject &&
        newValue !== fetchedValue &&
        (fetchedValue !== '' || newValue !== undefined) &&
        newValue !== ''
      ) {
        acc[key] = newValue;
      }

      return acc;
    }, {} as { [key: string]: any });


    console.log({ changedFields })

    if (id) {
      setFormErrors({});

      if (Object.keys(changedFields).length === 0) {
        handleSnackbar('No changes detected for update.', 'info');
        return;
      }

      dispatch(updateProject({ id, ...changedFields }));
    }
  };

  useEffect(() => {
    const handleUpdateStatus = () => {
      if (updateStatus === STATUS.SUCESS) {
        handleSnackbar(updateMessage, 'success');
        window.location.reload();
      }

      if (updateStatus === STATUS.FAILED) {
        handleSnackbar(updateMessage, 'error');
      }
    };

    handleUpdateStatus();
    return () => {
      if (updateStatus === STATUS.SUCESS || updateStatus === STATUS.FAILED) {
        dispatch(setUpdateProjectStatus(STATUS.NOT_STARTED));
      }
    };
  }, [updateStatus, navigate, dispatch]);

  const handleEnableTestChange = (event: ChangeEvent<HTMLInputElement>) => {
    const { name, checked } = event.target;

    setProjectDetails((prevDetails) => ({
      ...prevDetails,
      [name]: checked,
    }));
  };

  return (
    <>
      <div style={{ marginTop: "20px", marginLeft: "10px" }}>
        <div >
          <div style={{ display: 'flex', marginTop: '20px' }}>
            <FormField
              label="Test Duration (in mins)"
              placeholder="Enter Test Duration"
              name="test_time"
              value={projectDetails.test_time}
              onChange={handleFieldChange}
              error={formSubmitted && formErrors.test_time ? formErrors.test_time : ''}
              textFieldSx={{ marginRight: '20px' }}
              infoText='Enter the total time for the test'
            />
            <FormField
              label="Max Attempts"
              placeholder="Enter Max Attempts"
              name="test_max_attempts"
              value={projectDetails.test_max_attempts}
              onChange={handleFieldChange}
              error={formSubmitted && formErrors.test_max_attempts ? formErrors.test_max_attempts : ''}
              textFieldSx={{ marginRight: '20px' }}
              infoText='Set the maximum number of allowed attempts'
            />
            <FormField
              label="Passing Score (%)"
              placeholder="Enter Passing Score"
              name="test_passing_score_percentage"
              value={projectDetails.test_passing_score_percentage}
              onChange={handleFieldChange}
              textFieldSx={{ marginRight: '20px' }}
              error={formSubmitted && formErrors.test_passing_score_percentage ? formErrors.test_passing_score_percentage : ''}
              infoText='Define the minimum score required to pass'
            />
          </div>
          <div style={{ display: 'flex', marginTop: "20px" }}>
            <FormField
              label="Max Task Allocation"
              placeholder="Enter Max Task Allocation"
              name="auto_allocate_task_count"
              value={projectDetails.auto_allocate_task_count}
              onChange={handleFieldChange}
              error={formSubmitted && formErrors.auto_allocate_task_count ? formErrors.auto_allocate_task_count : ''}
              textFieldSx={{ marginRight: '20px' }}
              infoText='Specify the highest number of tasks that can be auto assigned once an expert passes the test'
            />
            <FormField
              label="Total Questions"
              placeholder="Enter Total Questions"
              name="test_question_count"
              value={projectDetails.test_question_count}
              onChange={handleFieldChange}
              error={formSubmitted && formErrors.test_question_count ? formErrors.test_question_count : ''}
              textFieldSx={{ marginRight: '20px' }}
              infoText='Enter the total number of questions in the test'
            />
          </div>
          <div style={{ display: 'flex', marginTop: "20px" }}>
            <FormField
              label="Easy Questions"
              placeholder="Enter Easy Questions"
              name="test_question_distribution.easy"
              value={projectDetails.test_question_distribution.easy}
              onChange={handleFieldChange}
              error={formSubmitted && formErrors['test_question_distribution.easy'] ? formErrors['test_question_distribution.easy'] : ''}
              textFieldSx={{ marginRight: '20px' }}
              infoText='Set the number of easy-level questions'
            />
            <FormField
              label="Medium Questions"
              placeholder="Enter Medium Questions"
              name="test_question_distribution.medium"
              value={projectDetails.test_question_distribution.medium}
              onChange={handleFieldChange}
              error={formSubmitted && formErrors['test_question_distribution.medium'] ? formErrors['test_question_distribution.medium'] : ''}
              textFieldSx={{ marginRight: '20px' }}
              infoText='Set the number of medium-level questions'
            />
            <FormField
              label="Difficult Questions"
              placeholder="Enter Difficult Questions"
              name="test_question_distribution.hard"
              value={projectDetails.test_question_distribution.hard}
              onChange={handleFieldChange}
              error={formSubmitted && formErrors['test_question_distribution.hard'] ? formErrors['test_question_distribution.hard'] : ''}
              textFieldSx={{ marginRight: '20px' }}
              infoText='Set the number of difficult-level questions'
            />
          </div>
          <div style={{ display: 'flex', justifyContent: 'flex-start', gap: 50 }}>
            {/* <div style={{ display: 'flex', marginTop: "20px" }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={projectDetails.enable_question_randomization}
                    onChange={handleCheckboxChange}
                    name="enable_question_randomization"
                    color="primary"
                  />
                }
                label={<div><div style={{ fontWeight: 900 }}>Enable Question Randomization</div>
                </div>}
                style={{ marginTop: '10px' }}
              />
            </div> */}
            <div style={{ display: 'flex', marginLeft: '20px', marginTop: "20px" }}>
              <FormControlLabel
                sx={{
                  // backgroundColor: projectDetails.enable_options_shuffling ? "lightskyblue" : "transparent",
                  border: projectDetails.enable_options_shuffling ? "2px solid black" : "2px solid transparent",
                  borderRadius: "7px",
                  transition: "all 0.3s ease",
                  padding: "3px 12px 3px 2px",
                }}
                control={
                  <Checkbox
                    checked={projectDetails.enable_options_shuffling}
                    onChange={handleCheckboxChange}
                    name="enable_options_shuffling"
                    sx={{
                      color: "black",
                      "&.Mui-checked": {
                        color: "black",
                      },
                    }}

                  />
                }
                label={<div><div style={{ fontWeight: 900 }}>Enable Options Shuffling</div>
                </div>}
                style={{ marginTop: '10px' }}
              />
            </div>
            <div style={{ display: 'flex', marginLeft: '20px', marginTop: "20px" }}>
              <FormControlLabel
                sx={{
                  // backgroundColor: projectDetails.enable_question_shuffling ? "lightskyblue" : "transparent",
                  border: projectDetails.enable_question_shuffling ? "2px solid black" : "2px solid transparent",
                  borderRadius: "7px",
                  transition: "all 0.3s ease",
                  padding: "3px 12px 3px 2px",
                }}
                control={
                  <Checkbox
                    checked={projectDetails.enable_question_shuffling}
                    onChange={handleCheckboxChange}
                    name="enable_question_shuffling"
                    color="primary"
                    sx={{
                      color: "black",
                      "&.Mui-checked": {
                        color: "black",
                      },
                    }}
                  />
                }
                label={<div><div style={{ fontWeight: 900 }}>Enable Question Shuffling</div>
                </div>}
                style={{ marginTop: '10px' }}
              />
            </div>
            <div style={{ display: 'flex', marginLeft: '20px', marginTop: "20px" }}>
              <FormControlLabel
                sx={{
                  // backgroundColor: projectDetails.is_test_enabled ? "lightskyblue" : "transparent",
                  border: projectDetails.is_test_enabled ? "2px solid black" : "2px solid transparent",
                  borderRadius: "7px",
                  transition: "all 0.3s ease",
                  padding: "3px 12px 3px 2px",
                }}
                control={
                  <Checkbox
                    checked={projectDetails.is_test_enabled}
                    onChange={handleEnableTestChange}
                    name="is_test_enabled"
                    color="primary"
                    sx={{
                      color: "black",
                      "&.Mui-checked": {
                        color: "black",
                      },
                    }}

                  />
                }

                label={<div><div style={{ fontWeight: 900 }}>Is Test Enable</div> <Typography style={{ fontSize: "10px", fontFamily: "Montserrat" }} >Mark for enable test.</Typography> </div>}

                style={{ marginTop: '10px' }}
              />
            </div>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'flex-start', marginTop: "20px" }}>
            <Typography sx={{ marginTop: '10px', marginLeft: '20px', fontFamily: 'Montserrat', fontSize: '14px', fontWeight: 600, lineHeight: '24px', letterSpacing: '0px', textAlign: 'left', color: '#10383D' }}>Test Guidelines</Typography>
            <Container component="main" maxWidth="xl">
              <CssBaseline />
              <RichTextEditor
                initialContent={path ? '' : fetchedProject.test_guidelines}
                onChange={handleTestGuidelines}

              />
              {formSubmitted && formErrors.test_guidelines && (
                <Typography variant="body2" color="error">
                  {formErrors.test_guidelines}
                </Typography>
              )}
            </Container>
          </div>
        </div>
        <Box mt={2} display="flex" justifyContent="flex-end" width="100%" sx={{ marginBottom: '10px' }}>
          <AddButton disabled={updateStatus === STATUS.PENDING} startIcon={updateStatus === STATUS.PENDING && <CircularProgress size={15} />} onClick={handleFormSubmit}>Update Test Settings</AddButton>
        </Box>
      </div>
      <SnackbarNotification
        open={snackbarOpen}
        message={snackbarMessage}
        severity={snackbarSeverity}
        onClose={handleSnackbarClose}
      />
    </>
  )
}

export default TestControl
